<template>
  <div class="bui-entity-listing-page playlist-editor-redesign">
    <content-with-loading-skeleton
      :isLoading="isLoading"
      loadingMessage="Loading Playlist Data..."
      :isWithMessage="true"
    >
      <template #loaded>
        <div class="playlist-editor-redesign__inner">
          <TopBar :isReadOnly="isReadOnly" />

          <InfoCard>
            <template #title>
              <h2 class="info-card__title-text">Playlist Items</h2>
            </template>
            <MediaBrowserButtonWithExpandable :isReadOnly="isReadOnly" />
            <PlaylistEditPane :isReadOnly="isReadOnly" />
          </InfoCard>

          <!-- Sticky footer -->
          <footer class="editor-footer">
            <div class="editor-footer__inner">
              <ExitButton
                v-bind="{ hasUnsavedChanges: isReadOnly ? false : hasUnsavedChanges, unsetHasUnsavedChanges }"
                @exit="onExit"
                @saveAndExit="onSaveAndExit"
              />
              <SavePlaylistButton v-if="!isReadOnly" ref="savePlaylistButtonRef" />
            </div>
          </footer>
        </div>
      </template>
    </content-with-loading-skeleton>
  </div>
</template>


<script setup lang="ts">
import MediaBrowserButtonWithExpandable from './MediaBrowser/MediaBrowserButtonWithExpandable.vue';
import PlaylistEditPane from './PlaylistEditPane/PlaylistEditPane.vue';
import TopBar from './TopBar/TopBar.vue';
import { onBeforeMount, ref, computed } from 'vue'
import { onBeforeRouteUpdate, useRoute } from 'vue-router'
import usePlaylist from './composables/playlist';
import useUser from '@/composables/user';
import SavePlaylistButton from './SavePlaylistButton/SavePlaylistButton.vue';
import ExitButton from '@/components/Dialog/ButtonsWithDialog/ExitButton/ExitButton.vue';
import InfoCard from '@/components/bui/molecules/InfoCard/InfoCard.vue';
import sharedRefs from './sharedRefs';
import router from '@/router';

const { loadPlaylist, initializeHasUnsavedChangesWatcher, hasUnsavedChanges, unsetHasUnsavedChanges, isLoading } = usePlaylist();
initializeHasUnsavedChangesWatcher();

const route = useRoute()
const user = useUser();
const isReadOnly = computed(() => !user.hasWriteAccessToArea('Playlists'));

const currentPlaylistId = route.params.id;
const { savePlaylistButtonRef } = sharedRefs;

function onExit() {
  router.push({ path: '/playlists' })
}

async function onSaveAndExit() {
  await savePlaylistButtonRef.value.savePlaylist();
  router.push({ path: '/playlists' })
}

onBeforeMount(async () => {
  await loadPlaylist(currentPlaylistId);
});

onBeforeRouteUpdate(async (newRoute) => {
  if(newRoute.name === "playlist-editor") {
    const newPlaylistId = newRoute.params.id;
    await loadPlaylist(newPlaylistId);
  }
})
</script>



<style lang="scss" scoped>
@import '@/assets/styles/bui-editor-template';

/* Outer wrapper: paints the BUI page surface edge-to-edge (see the note in
   PlayerEditorView — `.bui-entity-listing-page` is wired in PageLayout.vue). */
.playlist-editor-redesign {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  background: var(--bui-page-fill, #ff007b);
  color: var(--bui-page-head, #fffcfd);
  padding: var(--bui-page-pad-y, 32px) var(--bui-page-pad-x, 48px) 5rem;
  box-sizing: border-box;

  @media (max-width: 1100px) {
    padding-left: 24px;
    padding-right: 24px;
  }
}

.playlist-editor-redesign__inner {
  display: flex;
  flex-direction: column;
  gap: var(--bui-page-section-gap, 24px);
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
}
</style>
